import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheck,
    faQuestion,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import { updateAttendanceStatus } from '../../../../services/attendanceService';
import { useAuth } from '../../../../contexts/AuthContext';
import { useLanguage } from '../../../../contexts/LanguageContext';

function EventAttendees({ eventData, onEventDataUpdate, user: propUser, showOnlyCurrentUser = false }) {
    const { t } = useLanguage();
    // Get authenticated user from context (will take precedence over prop)
    const { user: authUser } = useAuth();
    const user = authUser || propUser; // Use authUser if available, fallback to prop
    
    // Helper function to safely compare user IDs
    const isCurrentUserAttendee = (attendeeUserId) => {
        if (!user) return false;
        
        // Try to get user ID from various possible properties
        const userId = user.id || user.user_id;
        
        return !!(userId && attendeeUserId && String(userId) === String(attendeeUserId));
    };
    
    // Function to handle status change for an attendee
    const handleStatusChange = async (userId, newStatus) => {
        try {
            await updateAttendanceStatus(eventData.id, userId, newStatus);
            
            // Update the parent component state to reflect the change
            const updatedEventData = {
                ...eventData,
                attendees: eventData.attendees.map(attendee => 
                    attendee.user_id === userId 
                        ? { ...attendee, status: newStatus } 
                        : attendee
                )
            };
            
            onEventDataUpdate(updatedEventData);
        } catch (error) {
        }
    };

    // Enhanced status icon with circle background
    const StatusIcon = ({ status }) => {
        let icon, bgColor;
        
        switch (status) {
            case 'confirmed':
                icon = faCheck;
                bgColor = '#28a745'; // green
                break;
            case 'declined':
                icon = faTimes;
                bgColor = '#dc3545'; // red
                break;
            default:
                icon = faQuestion;
                bgColor = '#6c757d'; // gray
                break;
        }
        
        return (
            <div 
                style={{
                    width: '28px', 
                    height: '28px', 
                    borderRadius: '50%', 
                    backgroundColor: bgColor,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px'
                }}
            >
                <FontAwesomeIcon icon={icon} />
            </div>
        );
    };

    // Modern toggle switch component
    const AttendanceToggle = ({ attendee }) => {
        const status = attendee.status || 'pending';
        const isConfirmed = status === 'confirmed';
        const isDeclined = status === 'declined';
        
        const handleToggleClick = () => {
            // If current status is confirmed, switch to declined
            // If current status is declined or pending, switch to confirmed
            const newStatus = isConfirmed ? 'declined' : 'confirmed';
            handleStatusChange(attendee.user_id, newStatus);
        };
        
        return (
            <div className="d-flex align-items-center">
                <span className="me-2" style={{
                    fontSize: '0.85rem',
                    color: isDeclined ? '#dc3545' : '#6c757d'
                }}>
                    {t('events.attendees.notAttending')}
                </span>
                
                {/* Toggle Switch */}
                <div 
                    className="attendance-toggle position-relative" 
                    style={{
                        width: '60px',
                        height: '28px',
                        borderRadius: '100px',
                        backgroundColor: isConfirmed ? '#28a745' : (isDeclined ? '#dc3545' : '#adb5bd'),
                        cursor: 'pointer',
                        transition: 'background-color 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 4px'
                    }}
                    onClick={handleToggleClick}
                >
                    <div 
                        className="toggle-thumb" 
                        style={{
                            position: 'absolute',
                            left: isConfirmed ? 'calc(100% - 24px)' : '4px',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            backgroundColor: '#fff',
                            transition: 'left 0.3s ease',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }}
                    ></div>
                </div>
                
                <span className="ms-2" style={{
                    fontSize: '0.85rem',
                    color: isConfirmed ? '#28a745' : '#6c757d',
                    fontWeight: isConfirmed ? 'bold' : 'normal'
                }}>
                    {t('events.attendees.attending')}
                </span>
            </div>
        );
    };

    // Get display name or username (now with the backend including display_name)
    const getAttendeeDisplayName = (attendee) => {
        return attendee.display_name || attendee.email?.split('@')[0] || t('events.attendees.defaultName');
    };

    // Render an attendee card
    const AttendeeCard = ({ attendee }) => {
        return (
            <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                <div className="d-flex align-items-center">
                    {/* User avatar with first letter of display name or username */}
                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" 
                        style={{ width: '32px', height: '32px' }}>
                        {getAttendeeDisplayName(attendee).charAt(0).toUpperCase()}
                    </div>
                    <span>
                        {getAttendeeDisplayName(attendee)}
                    </span>
                </div>
                
                {/* Show toggle switch only for current user, otherwise just the status icon */}
                <div className="ms-auto">
                    {isCurrentUserAttendee(attendee.user_id) ? (
                        <AttendanceToggle attendee={attendee} />
                    ) : (
                        <StatusIcon status={attendee.status} />
                    )}
                </div>
            </div>
        );
    };

    // If no attendees, show a message
    if (!eventData.attendees || eventData.attendees.length === 0) {
        return (
            <div className="event-attendees">
                <p className="text-muted text-center">{t('events.attendees.empty')}</p>
            </div>
        );
    }

    // Find the current user in the attendees list
    const currentUserAttendee = eventData.attendees.find(
        attendee => isCurrentUserAttendee(attendee.user_id)
    );
    
    // Filter other attendees (not the current user)
    const otherAttendees = eventData.attendees.filter(
        attendee => !isCurrentUserAttendee(attendee.user_id)
    );
    
    // Filter attendees based on showOnlyCurrentUser prop
    const displayedOtherAttendees = showOnlyCurrentUser ? [] : otherAttendees;

    return (
        <div className="event-attendees">
            <div className="row g-2">  
                {/* Current user's attendance - always visible */}
                {currentUserAttendee && (
                    <div className="col-12 mb-2">
                        <AttendeeCard attendee={currentUserAttendee} />
                    </div>
                )}
                
                {/* Show attendee count when collapsed */}
                {showOnlyCurrentUser && otherAttendees.length > 0 && (
                    <div className="col-12 mb-1">
                        <small className="text-muted">
                            {otherAttendees.length === 1
                                ? t('events.attendees.otherParticipant', { count: otherAttendees.length })
                                : t('events.attendees.otherParticipants', { count: otherAttendees.length })}
                        </small>
                    </div>
                )}
                
                {/* Other attendees - shown when not collapsed */}
                <div className="row g-2 w-100 m-0">
                    {displayedOtherAttendees.map((attendee) => (
                        <div key={attendee.user_id} className="col-md-6 px-0 mb-2">
                            <AttendeeCard attendee={attendee} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default EventAttendees;